import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaPrint,
  FaArrowRight,
  FaHome,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
} from "react-icons/fa";
import Header from "./header";
import Footer from "./Footer";
import { Helmet } from "react-helmet";

const styles = {
  page: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif",
    paddingBottom: "40px",
  },
  container: {
    maxWidth: "800px",
    margin: "0 auto",
    padding: "40px 20px",
    marginTop: "60px",
  },
  successCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "40px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0",
    marginBottom: "24px",
  },
  icon: { color: "#10b981", fontSize: "64px", marginBottom: "16px" },
  title: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#0f172a",
    margin: "0 0 8px 0",
  },
  subtitle: { color: "#64748B", fontSize: "16px" },

  detailsCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
  },
  sectionHeader: {
    backgroundColor: "#f1f5f9",
    padding: "16px 24px",
    fontSize: "16px",
    fontWeight: "700",
    color: "#334155",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  contentGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "32px",
    padding: "24px",
    borderBottom: "1px solid #f1f5f9",
  },
  label: {
    fontSize: "12px",
    textTransform: "uppercase",
    color: "#94a3b8",
    fontWeight: "700",
    marginBottom: "8px",
    letterSpacing: "0.5px",
  },
  value: {
    fontSize: "15px",
    color: "#0f172a",
    fontWeight: "500",
    lineHeight: "1.6",
  },

  itemRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 0",
    borderBottom: "1px dashed #e2e8f0",
  },
  itemName: { fontWeight: "600", color: "#334155" },
  itemMeta: { fontSize: "13px", color: "#94a3b8" },

  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "16px 0",
    fontSize: "18px",
    fontWeight: "800",
    color: "#0f172a",
    borderTop: "2px solid #e2e8f0",
    marginTop: "16px",
  },

  btnGroup: {
    display: "flex",
    gap: "16px",
    marginTop: "32px",
    justifyContent: "center",
  },
  primaryBtn: {
    backgroundColor: "#0f172a",
    color: "#fff",
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  secondaryBtn: {
    backgroundColor: "#fff",
    color: "#475569",
    padding: "12px 24px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};

const OrderConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const conversionSent = useRef(false);
  const { orderData } = location.state || {};

  // ✅ Hook 1
  useEffect(() => {
    if (!orderData) {
      navigate("/");
    }
  }, [orderData, navigate]);

  // useEffect(() => {
  //   if (!orderData || conversionSent.current) return;

  //   conversionSent.current = true;

  //   window.gtag("event", "conversion", {
  //     send_to: "AW-10903508518/LHk4CN7gnpIcEKasmc8o",
  //     value: 250.0,
  //     currency: "INR",
  //     transaction_id: orderId || "",
  //   });
  // }, [orderData]);

  // ❗ AFTER hooks
  if (!orderData) return null;

  const { orderId, paymentId, items, shipping, summary, date, shippingCharge } =
    orderData;

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(val);

  const handlePrint = () => {
    const printContent = printRef.current; // Get the content of the card
    const originalContents = document.body.innerHTML; // Store original body content

    // Temporarily replace body content with only the invoice
    document.body.innerHTML = printContent.innerHTML;

    // Apply print-specific styles
    const printStyles = `
      <style>
        body { margin: 0; padding: 0; }
        .invoice-actions { display: none !important; } /* Hide actions when printing */
        /* You might want to fine-tune these for better print appearance */
        .invoice-card {
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 20px !important;
            max-width: 100% !important;
            margin: 0 !important;
        }
        /* Add other necessary print styles here */
        h1, h2, h3, h4, h5, h6 { color: #000 !important; }
        p, span { color: #333 !important; }
        table { border-collapse: collapse !important; width: 100% !important; }
        th, td { border: 1px solid #eee !important; padding: 8px 10px !important; font-size: 11px !important; }
        th { background-color: #f0f0f0 !important; color: #000 !important; }
        .grand-total-value { color: #000 !important; } /* Ensure total is black */
        .logo-img { filter: none !important; width: 180px !important; }
        .address-block { background: none !important; box-shadow: none !important; }
        /* Hide Header and Footer if they are directly included in the original DOM and not handled by their own print media queries */
        .app-header, .app-footer { display: none !important; }
      </style>
    `;
    document.head.insertAdjacentHTML("beforeend", printStyles);

    window.print(); // Trigger print dialog

    // Restore original body content and remove temporary styles after printing
    document.body.innerHTML = originalContents;
    window.location.reload(); // A simple way to restore the page state
  };

  const districtsData = JSON.parse(localStorage.getItem("districts") || "[]");

  const savedCities = JSON.parse(localStorage.getItem("cities") || "[]");

  const statesData = JSON.parse(localStorage.getItem("states") || "[]");

  // Find city
  const cityObj = savedCities.find(
    (city) => String(city.id) === String(shipping.city),
  );

  // Find state
  const stateObj = statesData.find(
    (state) => String(state.state_id) === String(shipping.state),
  );

  // Correct fields
  const cityName = cityObj?.name;
  const stateName = stateObj?.state_title;

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Order Confirmed | CGRMART</title>

        <meta
          name="description"
          content="Your order has been successfully placed with CGRMART. View order details, payment confirmation, and receipt."
        />

        <meta
          name="keywords"
          content="order confirmation, EV charger order, CGRMART order success, Tucker charger purchase"
        />

        <meta name="author" content="CGRMART" />

        {/*VERY IMPORTANT (PRIVATE PAGE) */}
        

        {/* Open Graph (WhatsApp / Facebook) */}
        <meta property="og:title" content="Order Confirmed | CGRMART" />
        <meta
          property="og:description"
          content="Your order has been successfully placed. Check your order summary and receipt."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />

        {/* Twitter */}
        <meta name="twitter:title" content="Order Confirmed | CGRMART" />
        <meta
          name="twitter:description"
          content="Order placed successfully. View your receipt and details."
        />

        {/* Canonical */}
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <Header />
      <div style={styles.page}>
        <style>{`
          @media (max-width: 768px) {
            .order-success-container { max-width: 100% !important; padding: 30px 15px !important;}
            .order-success-card { padding: 25px 20px !important; }
            .order-success-icon { font-size: 48px !important; }
            .order-success-title { font-size: 24px !important; }
            .order-success-content-grid { grid-template-columns: 1fr !important; gap: 20px !important; padding: 20px !important; }
            .order-success-btn-group { flex-direction: column !important; }
            .order-success-btn-group button { width: 100% !important; }
          }
          @media (max-width: 480px) {
            .order-success-container { padding: 20px 10px !important; }
            .order-success-card { padding: 20px 15px !important; }
            .order-success-icon { font-size: 40px !important; }
            .order-success-title { font-size: 20px !important; }
          }
        `}</style>
        <div
          style={styles.container}
          ref={printRef}
          className="order-success-container"
        >
          {/* SUCCESS BANNER */}
          <div style={styles.successCard} className="order-success-card">
            <FaCheckCircle style={styles.icon} className="order-success-icon" />
            <h1 style={styles.title} className="order-success-title">
              Order Confirmed!
            </h1>
            <p style={styles.subtitle}>
              Thank you for your purchase. Your order has been placed
              successfully.
            </p>
            <div
              style={{
                marginTop: "20px",
                padding: "8px 16px",
                background: "#f0fdf4",
                display: "inline-block",
                borderRadius: "20px",
                color: "#166534",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              Order ID: {orderId}
            </div>
          </div>

          {/* ORDER DETAILS */}
          <div style={styles.detailsCard}>
            <div style={styles.sectionHeader}>
              <span>Order Summary</span>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#64748B",
                }}
              >
                Order Date: {date}
              </span>
            </div>

            <div
              style={styles.contentGrid}
              className="order-success-content-grid"
            >
              {/* Shipping Info */}
              <div>
                <div style={styles.label}>Shipping To</div>
                <div style={styles.value}>
                  <strong>{shipping.name}</strong>
                  <br />
                  {shipping.address}
                  <br />
                  {cityName}, {stateName} - {shipping.pincode}
                </div>
                <div
                  style={{
                    marginTop: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      ...styles.value,
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Phone: {shipping.mobile}
                  </div>
                  <div
                    style={{
                      ...styles.value,
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Email: {shipping.email}
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div>
                <div style={styles.label}>Payment Details</div>
                <div style={styles.value}>
                  Method: Online (Razorpay)
                  <br />
                  Payment ID:{" "}
                  <span
                    style={{
                      fontFamily: "monospace",
                      background: "#f1f5f9",
                      padding: "2px 4px",
                      borderRadius: "4px",
                    }}
                  >
                    {paymentId}
                  </span>
                  <br />
                  Status:{" "}
                  <span style={{ color: "#10b981", fontWeight: "700" }}>
                    Paid
                  </span>
                </div>
              </div>
            </div>

            {/* Items List */}
            <div style={{ padding: "24px" }}>
              <div style={styles.label}>Items Ordered</div>
              {items.map((item, index) => (
                <div key={index} style={styles.itemRow}>
                  <div>
                    <div style={styles.itemName}>{item.name}</div>
                    <div style={styles.itemMeta}>
                      Qty: {item.quantity} × {formatCurrency(item.unitPrice)}
                    </div>
                  </div>
                  <div style={{ fontWeight: "600", color: "#0f172a" }}>
                    {formatCurrency(item.unitPrice * item.quantity)}
                  </div>
                </div>
              ))}

              {/* Totals */}
              <div style={{ marginTop: "20px", paddingLeft: "40%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                    fontSize: "14px",
                    color: "#64748B",
                  }}
                >
                  <span>Subtotal (GST Included)</span>
                  <span>{formatCurrency(summary.total)}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                    fontSize: "14px",
                    color: "#64748B",
                  }}
                >
                  <span>Shipping</span>
                  <span>{formatCurrency(shippingCharge)}</span>
                </div>

                <div style={styles.totalRow}>
                  <span>Total Paid (GST Included)</span>
                  <span>{formatCurrency(summary.total + shippingCharge)}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.btnGroup} className="order-success-btn-group">
            <button style={styles.secondaryBtn} onClick={handlePrint}>
              <FaPrint /> Print Receipt
            </button>
            <button style={styles.primaryBtn} onClick={() => navigate("/")}>
              <FaHome /> Continue Shopping <FaArrowRight />
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderConfirmationPage;
